import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ProfileEditor({ onClose }) {
  const { user, updateProfile } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [photo, setPhoto] = useState(user?.profile_photo || "");
  const [saving, setSaving] = useState(false);
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const dialog = useRef(null);
  const opener = useRef(document.activeElement);
  useEffect(() => {
    const element = dialog.current;
    element.showModal();
    return () => { element.close(); opener.current?.focus(); };
  }, []);
  const choosePhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 10 * 1024 * 1024) {
      toast.error("Escolha uma foto JPG, PNG ou WebP de até 10 MB");
      return;
    }
    setLoadingPhoto(true);
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = url; });
      const canvas = document.createElement("canvas");
      canvas.width = 256; canvas.height = 256;
      const ctx = canvas.getContext("2d");
      const side = Math.min(img.naturalWidth, img.naturalHeight);
      if (!side || !ctx) throw new Error("Foto inválida");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, 256, 256);
      ctx.drawImage(img, (img.naturalWidth-side)/2, (img.naturalHeight-side)/2, side, side, 0, 0, 256, 256);
      setPhoto(canvas.toDataURL("image/jpeg", 0.85));
    } catch { toast.error("Não foi possível abrir esta foto"); }
    finally { URL.revokeObjectURL(url); setLoadingPhoto(false); }
  };
  const save = async (event) => {
    event.preventDefault();
    if (saving || loadingPhoto || !name.trim()) return;
    setSaving(true);
    try {
      await updateProfile({ name: name.trim(), profile_photo: photo });
      toast.success("Perfil salvo"); onClose();
    } catch (error) { toast.error(error.response?.data?.detail || "Não foi possível salvar o perfil"); }
    finally { setSaving(false); }
  };
  return (
    <dialog ref={dialog} onCancel={(event) => { if (saving || loadingPhoto) event.preventDefault(); else onClose(); }} aria-labelledby="profile-title" className="w-[calc(100%-2rem)] max-w-sm rounded-xl border border-pink-500/30 bg-slate-950 p-5 text-slate-100 backdrop:bg-black/70">
      <form onSubmit={save} className="space-y-4">
        <h2 id="profile-title" className="text-lg font-semibold text-pink-300">Meu perfil</h2>
        <div className="flex items-center gap-4">
          {photo ? <img src={photo} alt="Prévia da foto de perfil" className="h-20 w-20 rounded-full object-cover" /> : <div className="grid h-20 w-20 place-items-center rounded-full bg-pink-500/20 text-2xl text-pink-300">{name.trim().slice(0,1).toUpperCase() || "?"}</div>}
          <div className="min-w-0 space-y-2">
            <label className="block text-sm">Foto de perfil
              <input type="file" accept="image/jpeg,image/png,image/webp" disabled={saving || loadingPhoto} onChange={choosePhoto} className="mt-2 block w-full text-xs" />
            </label>
            {photo && <button type="button" disabled={saving || loadingPhoto} onClick={() => setPhoto("")} className="text-xs text-pink-300 underline">Remover foto</button>}
          </div>
        </div>
        <label className="block text-sm">Como você quer aparecer
          <input autoFocus required maxLength={80} value={name} disabled={saving} onChange={(event) => setName(event.target.value)} className="mt-2 w-full rounded border border-slate-700 bg-slate-900 p-2 text-white" />
        </label>
        <div className="flex justify-end gap-3">
          <button type="button" disabled={saving || loadingPhoto} onClick={onClose}>Cancelar</button>
          <button type="submit" disabled={saving || loadingPhoto || !name.trim()} className="rounded bg-pink-500 px-4 py-2 text-white disabled:opacity-50">{saving ? "Salvando..." : loadingPhoto ? "Preparando foto..." : "Salvar"}</button>
        </div>
      </form>
    </dialog>
  );
}
